import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import './FacultyDetailPage.css';

// A helper component for the 5-star rating select
const RatingSelect = ({ label, value, onChange }) => (
  <div className="form-group">
    <label>{label}</label>
    <select
      value={value}
      onChange={onChange}
      className="review-select"
    >
      <option value="5">5 - Excellent</option>
      <option value="4">4 - Good</option>
      <option value="3">3 - Average</option>
      <option value="2">2 - Poor</option>
      <option value="1">1 - Terrible</option>
    </select>
  </div>
);

const FacultyDetailPage = () => {
  const [faculty, setFaculty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [teaching, setTeaching] = useState(5);
  const [evaluation, setEvaluation] = useState(5);
  const [behaviour, setBehaviour] = useState(5);
  const [internals, setInternals] = useState(5);
  const [submitting, setSubmitting] = useState(false);

  const [sortType, setSortType] = useState('newest');

  const { id } = useParams();

  // --- useEffect hook containing the fetch logic ---
  useEffect(() => {
    // Define the async function *inside* the effect
    const loadFaculty = async () => {
      try {
        setLoading(true);
        setError(''); // Clear previous errors
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const res = await axios.get(`${apiUrl}/api/faculty/${id}`); // Use the specific ID
        setFaculty(res.data);
      } catch (err) {
        console.error('Error fetching faculty details:', err);
        setError('Faculty not found or error loading data.');
      } finally {
        // Make sure loading is set to false even if there's an error
        setLoading(false);
      }
    };

    // Call the async function
    loadFaculty();

  }, [id]); // Now the dependency array only needs 'id'
  // --- End of useEffect ---

  // --- handleSubmitReview (Unchanged from your version) ---
  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const newReview = { teaching, evaluation, behaviour, internals };
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await axios.post(`${apiUrl}/api/faculty/${id}/reviews`, newReview);

      setFaculty(res.data);
      setTeaching(5);
      setEvaluation(5);
      setBehaviour(5);
      setInternals(5);
      setSubmitting(false);
    } catch (err) {
      console.error('Error submitting review:', err.response?.data || err.message); // Log error data or message
      alert(err.response?.data?.message || 'Failed to submit review.'); // Show server message or generic one
      setSubmitting(false);
    }
  };

  // --- getSortedReviews (Unchanged from your version) ---
  const getSortedReviews = () => {
    if (!faculty || !faculty.reviews) return [];

    const reviewsToSort = [...faculty.reviews];

    if (sortType === 'highest') {
      return reviewsToSort.sort((a, b) => (b.average ?? 0) - (a.average ?? 0)); // Use ?? for nullish coalescing
    }
    if (sortType === 'lowest') {
      return reviewsToSort.sort((a, b) => (a.average ?? 0) - (b.average ?? 0)); // Use ?? for nullish coalescing
    }
    return reviewsToSort;
  };

  // --- Loading/Error/Data Checks (Unchanged) ---
  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!faculty) return null;

  // --- JSX Return (Unchanged from your version, includes safety checks) ---
  return (
    <div className="faculty-detail-container">
      <Link to="/faculty" className="back-link">&larr; Back to all faculty</Link>

      <header className="faculty-detail-header">
        <div className="header-info">
          <h2>{faculty.name}</h2>
          <p>{faculty.department}</p>
        </div>
        <div className="header-rating">
          <span>Overall Rating</span>
          <strong>
            {typeof faculty.overallRating === 'number'
              ? faculty.overallRating.toFixed(1)
              : 'N/A'}
          </strong>
          <span>({faculty.reviews ? faculty.reviews.length : 0} reviews)</span>
        </div>
      </header>

      <div className="faculty-detail-content">

        <div className="review-form-card">
          <h4>Rate this Faculty</h4>
          <form onSubmit={handleSubmitReview}>
            <RatingSelect label="Teaching" value={teaching} onChange={(e) => setTeaching(e.target.value)} />
            <RatingSelect label="Evaluation" value={evaluation} onChange={(e) => setEvaluation(e.target.value)} />
            <RatingSelect label="Behaviour" value={behaviour} onChange={(e) => setBehaviour(e.target.value)} />
            <RatingSelect label="Internals" value={internals} onChange={(e) => setInternals(e.target.value)} />
            <button type="submit" className="btn-submit-review" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Anonymous Rating'}
            </button>
          </form>
        </div>

        <div className="review-list-card">
          <h4>All Anonymous Ratings</h4>

          <div className="sort-buttons-container">
            <button
              className={`sort-button ${sortType === 'newest' ? 'active' : ''}`}
              onClick={() => setSortType('newest')}
            >
              Newest
            </button>
            <button
              className={`sort-button ${sortType === 'highest' ? 'active' : ''}`}
              onClick={() => setSortType('highest')}
            >
              Highest Rating
            </button>
            <button
              className={`sort-button ${sortType === 'lowest' ? 'active' : ''}`}
              onClick={() => setSortType('lowest')}
            >
              Lowest Rating
            </button>
          </div>

          {/* Corrected check for reviews */}
          {!faculty.reviews || faculty.reviews.length === 0 ? (
            <p>No ratings yet. Be the first!</p>
          ) : (
            <div className="review-list">
              {getSortedReviews().map(review => (
                <div className="review-item" key={review._id}>
                  <div className="review-header">
                    <strong className="review-rating">
                      {/* Use ?? for safety here too */}
                      {(review.average ?? 0).toFixed(1)} / 5.0
                    </strong>
                    <span className="review-date">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="review-ratings-grid">
                    <span>Teaching: <strong>{review.teaching ?? 'N/A'}</strong></span>
                    <span>Evaluation: <strong>{review.evaluation ?? 'N/A'}</strong></span>
                    <span>Behaviour: <strong>{review.behaviour ?? 'N/A'}</strong></span>
                    <span>Internals: <strong>{review.internals ?? 'N/A'}</strong></span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default FacultyDetailPage;